import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import {
  pcmpProjectFormSchema,
  type PcmpProjectFormValues,
} from '../../types/pcmpProjectForm.schema';
import type {
  PcmpCatalogItem,
  PcmpProjectDetail,
  PcmpProjectOptions,
} from '../../types/pcmpProject.types';
import { pcmpPriceKey } from '../../utils/pcmpPriceGrid';
import { PcmpPriceGridSection } from './PcmpPriceGridSection';
import { PcmpProjectAvailabilitySection } from './PcmpProjectAvailabilitySection';
import { PcmpProjectDetailsSection } from './PcmpProjectDetailsSection';

export interface PcmpProjectFormHandle {
  reset: (values?: PcmpProjectFormValues) => void;
}

interface PcmpProjectFormProps {
  defaultValues: PcmpProjectFormValues;
  options: PcmpProjectOptions;
  /** The project being edited: its own categories and classes stay offered even if inactive. */
  project?: PcmpProjectDetail;
  onSubmit: (values: PcmpProjectFormValues) => void;
  onCancel: () => void;
  submitLabel: string;
  pendingLabel: string;
  isPending: boolean;
}

/** The active options first, then any the project still uses that are no longer offered. */
function withProjectItems(
  offered: PcmpCatalogItem[],
  used: PcmpCatalogItem[] | undefined,
): PcmpCatalogItem[] {
  const offeredIds = new Set(offered.map((item) => item.id));
  return [...offered, ...(used ?? []).filter((item) => !offeredIds.has(item.id))];
}

export const PcmpProjectForm = forwardRef<PcmpProjectFormHandle, PcmpProjectFormProps>(
  function PcmpProjectForm(
    { defaultValues, options, project, onSubmit, onCancel, submitLabel, pendingLabel, isPending },
    ref,
  ) {
    const form = useForm<PcmpProjectFormValues>({
      resolver: zodResolver(pcmpProjectFormSchema),
      defaultValues,
      mode: 'onBlur',
    });

    const isDirty = form.formState.isDirty;
    const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty && !isPending);

    useImperativeHandle(ref, () => ({ reset: (values) => form.reset(values) }), [form]);

    const categories = useMemo(
      () => withProjectItems(options.categories, project?.categories),
      [options.categories, project?.categories],
    );
    const classes = useMemo(
      () => withProjectItems(options.classes, project?.classes),
      [options.classes, project?.classes],
    );
    const lockedKeys = useMemo(
      () =>
        new Set(
          (project?.prices ?? [])
            .filter((price) => price.inUse)
            .map((price) => pcmpPriceKey(price.categoryId, price.classId)),
        ),
      [project?.prices],
    );

    function handleInvalid() {
      requestAnimationFrame(() => {
        document
          .querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    return (
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, handleInvalid)}
          className="flex flex-1 flex-col gap-6"
          aria-busy={isPending || undefined}
        >
          <fieldset disabled={isPending} className="contents">
            <PcmpProjectDetailsSection options={options} />
            <PcmpProjectAvailabilitySection />
            <PcmpPriceGridSection
              categories={categories}
              classes={classes}
              lockedKeys={lockedKeys}
            />
          </fieldset>

          <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t bg-background/95 px-1 py-3 pb-[max(--spacing(3),env(safe-area-inset-bottom))] backdrop-blur">
            <Button
              type="button"
              variant="outline"
              onClick={() => askBeforeClose(onCancel)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending} className="gap-1.5">
              {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {isPending ? pendingLabel : submitLabel}
            </Button>
          </div>
        </form>
        {discardChangesDialog}
      </FormProvider>
    );
  },
);
